
import React, { useState, useEffect } from 'react';
import { Plus, Search, Mail, Edit, Trash2, Send, Loader2, CheckCircle2, X, Check, CircleOff, UserCheck, Clock, CalendarDays, Landmark, Building2, Phone, ShieldCheck, UserCog, Users, Star, ChevronRight, MessageCircle, Copy } from 'lucide-react';
import StaffLoginStatus from '../components/StaffLoginStatus';
import { StaffDutyChips, StaffDutyEditor } from '../components/StaffDuties';
import Modal from '../components/ui/Modal';
import Dialog from '../components/ui/Dialog';
import { api, Duty, Staff, Store, Position, StaffSkill } from '../api';
import { useToast } from '../contexts/ToastContext';
import Input from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';

const StaffPage: React.FC = () => {
    const { currentUser } = useAuth();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [allStaff, setAllStaff] = useState<Staff[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [positions, setPositions] = useState<Position[]>([]);
    const [duties, setDuties] = useState<Duty[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [storeFilterId, setStoreFilterId] = useState<string>('all');
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSendingInvite, setIsSendingInvite] = useState(false);
    const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
    const [originalStaff, setOriginalStaff] = useState<Staff | null>(null);
    const [myProfile, setMyProfile] = useState<Staff | null>(null);

    // 新規スタッフ追加フォームの state
    const [newStaffForm, setNewStaffForm] = useState({
        lastName: '',
        firstName: '',
        email: '',
        storeId: '',
    });

    const [dialog, setDialog] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void}>({ 
        isOpen: false, title: '', message: '', onConfirm: () => {} 
    });

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const [s, st, pos] = await Promise.all([
                    api.getStores(), 
                    api.getStaff(),
                    api.getPositions()
                ]);
                setDuties(await api.getDuties());
                setStores(s);
                setAllStaff(st);
                setPositions(pos);
                if (currentUser) {
                    const me = st.find(u => u.id === currentUser.uid);
                    setMyProfile(me || null);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [currentUser]);

    const processSave = async (staffToSave: Staff, removedStoreIds: string[]) => {
        if (!staffToSave.lastName?.trim() || !staffToSave.firstName?.trim() || !staffToSave.email?.trim()) {
            showToast('姓名、メールアドレスは必須項目です。', 'error');
            return;
        }

        setIsLoading(true);
        try {
            if (originalStaff?.role === 'system_admin' && staffToSave.role !== 'system_admin') {
                const otherAdmins = allStaff.filter(s => s.role === 'system_admin' && s.id !== staffToSave.id);
                if (otherAdmins.length === 0) {
                    showToast('システム管理者は最低1名必要です。', 'error');
                    setIsLoading(false);
                    return;
                }
            }

            if (removedStoreIds.length > 0) {
                await Promise.all(removedStoreIds.map(sid => api.cleanupStaffStoreData(staffToSave.id, sid)));
            }

            const finalSkills = (staffToSave.skills || []).filter(sk => staffToSave.positionIds.includes(sk.positionId));
            
            const finalStaff = { ...staffToSave, skills: finalSkills };
            await api.saveStaff([finalStaff]);
            
            setAllStaff(prev => {
                const index = prev.findIndex(s => s.id === finalStaff.id);
                if (index > -1) {
                    const newArr = [...prev];
                    newArr[index] = finalStaff;
                    return newArr;
                }
                return [...prev, finalStaff];
            });
            setIsModalOpen(false);
            showToast('保存しました。', 'success');
        } catch (e) {
            showToast('保存に失敗しました。', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveRequest = async () => {
        if (!editingStaff) return;
        const removedStoreIds = originalStaff?.stores?.filter(sid => !editingStaff.stores?.includes(sid)) || [];
        if (removedStoreIds.length > 0) {
            const storeNames = removedStoreIds.map(id => stores.find(s => s.id === id)?.name).join('、');
            setDialog({
                isOpen: true,
                title: '店舗所属の解除確認',
                message: `以下の店舗の所属を解除しますか？\n対象店舗：${storeNames}\n\n解除すると、その店舗での「未来のシフト」はすべて削除されます。`,
                onConfirm: () => {
                    setDialog(prev => ({ ...prev, isOpen: false }));
                    processSave(editingStaff, removedStoreIds);
                }
            });
            return;
        }
        processSave(editingStaff, []);
    };

    // 招待URLの発行後など、スタッフ情報だけを更新する。
    const refreshStaff = async () => {
        const st = await api.getStaff();
        setAllStaff(st);
        // 編集中の内容は保ったまま、連携・招待の状態だけを最新にする。
        setEditingStaff(prev => {
          const latest = prev ? st.find(x => x.id === prev.id) : undefined;
          return prev && latest ? { ...prev, lineStatus: latest.lineStatus, accountStatus: latest.accountStatus, inviteToken: latest.inviteToken } : prev;
        });
    };

    // 別タブでの招待の受諾・LINE連携などを一覧に反映する。
    useEffect(() => api.subscribeStaff(() => { refreshStaff(); }), []);

    const handleDelete = (staff: Staff) => {
        if (staff.role === 'system_admin') {
            const otherAdmins = allStaff.filter(s => s.role === 'system_admin' && s.id !== staff.id);
            if (otherAdmins.length === 0) {
                showToast('唯一のシステム管理者は削除できません。', 'error');
                return;
            }
        }

        setDialog({
            isOpen: true,
            title: 'スタッフ削除',
            message: `${staff.lastName} ${staff.firstName} を削除しますか？`,
            onConfirm: async () => {
                try {
                    await api.deleteStaff(staff.id);
                    setAllStaff(prev => prev.filter(s => s.id !== staff.id));
                    showToast('削除しました。', 'success');
                } catch (e) {
                    showToast('削除に失敗しました。', 'error');
                }
                setDialog(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleOpenAddModal = () => {
        // デフォルトの所属店舗：store_manager の場合は自分の最初の店舗、system_admin は選択させる
        const defaultStoreId = isSystemAdmin ? (stores[0]?.id || '') : (myProfile?.stores?.[0] || '');
        setNewStaffForm({ lastName: '', firstName: '', email: '', storeId: defaultStoreId });
        setIsAddModalOpen(true);
    };

    const handleAddStaff = async () => {
        const { lastName, firstName, email, storeId } = newStaffForm;
        if (!lastName.trim() || !firstName.trim() || !email.trim() || !storeId) {
            showToast('姓名、メールアドレス、所属店舗は必須です。', 'error');
            return;
        }

        // store_manager は自分の所属店舗にのみ追加可能
        if (!isSystemAdmin && !myProfile?.stores?.includes(storeId)) {
            showToast('自分の所属店舗にのみスタッフを追加できます。', 'error');
            return;
        }

        setIsSendingInvite(true);
        try {
            const staffId = `staff_${Date.now()}_${Math.random().toString(36).substring(7)}`;
            const avatarColors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500'];
            const color = avatarColors[Math.floor(Math.random() * avatarColors.length)];
            const newStaff: Staff = {
                id: staffId,
                lastName,
                firstName,
                email,
                stores: [storeId],
                role: 'staff',
                status: 'active',
                lineStatus: 'unconnected',
                accountStatus: 'none',
                positionIds: [],
                skills: [],
                avatarColor: color,
                hourlyWage: 0,
                paidLeaveCount: 0,
                limit: 0,
                priority: 'medium',
                insuranceType: '',
                tags: [],
                isGuest: false,
                employmentType: 'part_time',
            };
            await api.saveStaff([newStaff]);
            setAllStaff(prev => [...prev, newStaff]);
            setIsAddModalOpen(false);
            showToast(`${lastName} ${firstName} さんを追加しました。`, 'success');

            // 招待メールは任意（失敗してもスタッフ追加は成功扱い）。LINEを使わない店舗ではアカウント作成用の招待になる。
            try {
                const inviteUrl = await api.generateInviteToken(staffId);
                const store = stores.find(s => s.id === storeId);
                await api.sendInviteEmail({
                    email,
                    staffName: `${lastName} ${firstName}`,
                    inviteUrl,
                    storeName: store?.name || '店舗',
                });
                await refreshStaff();
            } catch (inviteErr) {
                console.warn('招待メール送信に失敗しましたが、スタッフは追加済みです', inviteErr);
            }
        } catch (e) {
            console.error(e);
            showToast('スタッフの追加に失敗しました。', 'error');
        } finally {
            setIsSendingInvite(false);
        }
    };

    const handleLevelChange = (positionId: string, level: number) => {
        if (!editingStaff) return;
        const currentSkills = [...(editingStaff.skills || [])];
        const skillIndex = currentSkills.findIndex(s => s.positionId === positionId);
        
        if (skillIndex > -1) {
            currentSkills[skillIndex] = { ...currentSkills[skillIndex], level };
        } else {
            currentSkills.push({ positionId, level });
        }
        setEditingStaff({ ...editingStaff, skills: currentSkills });
    };

    const filteredStaff = allStaff.filter(s => {
        const matchesSearch = `${s.lastName} ${s.firstName}`.includes(searchQuery) || s.email?.includes(searchQuery) || s.phoneNumber?.includes(searchQuery);
        const matchesStore = storeFilterId === 'all' || s.stores?.includes(storeFilterId);
        const isMyStoreMember = myProfile?.role === 'system_admin' || s.stores?.some(sid => myProfile?.stores?.includes(sid));
        return matchesSearch && matchesStore && isMyStoreMember;
    });

    const getPositionText = (staff: Staff) => {
        const pNames = (staff.positionIds || [])
          .map(pid => positions.find(p => p.id === pid)?.name)
          .filter(Boolean);
        
        if (pNames.length === 0) return 'なし';
        if (pNames.length <= 2) return pNames.join('、');
        return `${pNames.slice(0, 2).join('、')}ほか${pNames.length - 2}件`;
    };

    const getStatusBadge = (status?: string) => {
        switch (status) {
            case 'active': return <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100">在籍中</span>;
            case 'on_leave': return <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-100">休職中</span>;
            case 'resigned': return <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">退職済み</span>;
            default: return <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100">在籍中</span>;
        }
    };

    const labelClass = "text-xs font-bold text-slate-500 mb-1.5 block";
    const selectClass = "w-full bg-white border border-slate-300 rounded-[4px] px-3 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all";

    const isSystemAdmin = myProfile?.role === 'system_admin';
    const isSaveDisabled = !editingStaff?.lastName?.trim() || !editingStaff?.firstName?.trim() || !editingStaff?.email?.trim();

    return (
        <div className="animate-in">
            <Dialog isOpen={dialog.isOpen} type="confirm" title={dialog.title} message={dialog.message} onConfirm={dialog.onConfirm} onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} isDestructive />
            
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-slate-800">スタッフ管理</h1>
                    <select
                        value={storeFilterId}
                        onChange={(e) => setStoreFilterId(e.target.value)}
                        className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600 outline-none focus:ring-2 focus:ring-blue-100 transition-all shadow-sm"
                    >
                        <option value="all">全店舗を表示</option>
                        {stores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <button
                    onClick={handleOpenAddModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-[4px] text-sm font-bold hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                >
                    <Plus size={16} />
                    スタッフを追加
                </button>
            </div>

            <div className="bg-white rounded-[4px] shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center bg-gray-50/50">
                    <Search className="text-gray-400 mr-2" size={20} />
                    <input type="text" placeholder="名前や電話番号、メールで検索" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 outline-none text-sm bg-transparent font-bold" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left whitespace-nowrap lg:whitespace-normal">
                        <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="p-4 whitespace-nowrap">氏名</th>
                                <th className="p-4 whitespace-nowrap">ポジション</th>
                                <th className="p-4 whitespace-nowrap">対応業務</th>
                                <th className="p-4 whitespace-nowrap">所属店舗</th>
                                <th className="p-4 text-center whitespace-nowrap">ログイン</th>
                                <th className="p-4 text-right whitespace-nowrap">時給</th>
                                <th className="p-4 text-center whitespace-nowrap">有給</th>
                                <th className="p-4 text-center whitespace-nowrap">操作</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredStaff.map(staff => (
                                <tr key={staff.id} className="hover:bg-gray-50">
                                    <td className="p-4 font-bold text-slate-800 whitespace-nowrap w-px">
                                        <div className="flex items-center">
                                            <div className="flex items-center gap-2">
                                                {staff.lastName} {staff.firstName}
                                                {getStatusBadge(staff.status)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-wrap gap-1">{(staff.positionIds || []).map(pid => positions.find(p => p.id === pid)?.name).filter(Boolean).map(n => <span key={n} className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded whitespace-nowrap">{n}</span>)}{(staff.positionIds || []).length === 0 && <span className="text-[11px] text-slate-300 font-bold">なし</span>}</div>
                                    </td>
                                    <td className="p-4"><StaffDutyChips staff={staff} duties={duties} /></td>
                                    <td className="p-4">
                                        <div className="flex flex-wrap gap-1">
                                            {staff.stores?.map(sid => (
                                                <span key={sid} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold border border-slate-200 whitespace-nowrap">
                                                    {stores.find(s => s.id === sid)?.name || '店舗不明'}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="p-4 text-center">
                                        <StaffLoginStatus staff={staff} stores={stores} onChanged={refreshStaff} />
                                    </td>
                                    <td className="p-4 text-right font-mono font-bold text-slate-600 whitespace-nowrap">
                                        {(staff.hourlyWage || 0).toLocaleString()}円
                                    </td>
                                    <td className="p-4 font-bold text-emerald-600 text-center whitespace-nowrap">{staff.paidLeaveCount || 0}日</td>
                                    <td className="p-4 text-center">
                                        <button onClick={() => { setEditingStaff({ ...staff }); setOriginalStaff({ ...staff }); setIsModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-[4px] transition-colors"><Edit size={18} /></button>
                                        <button onClick={() => handleDelete(staff)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-[4px] transition-colors"><Trash2 size={18} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 新規スタッフ追加モーダル */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="スタッフを追加" maxWidth="max-w-md">
                <div className="pt-2 space-y-5">
                    <p className="text-sm text-slate-500 font-bold">入力したメールアドレスに招待メールを送信します。</p>
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="姓" requiredLabel value={newStaffForm.lastName} onChange={(e) => setNewStaffForm(f => ({ ...f, lastName: e.target.value }))} />
                        <Input label="名" requiredLabel value={newStaffForm.firstName} onChange={(e) => setNewStaffForm(f => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <Input label="メールアドレス" requiredLabel type="email" value={newStaffForm.email} onChange={(e) => setNewStaffForm(f => ({ ...f, email: e.target.value }))} placeholder="example@shiftmate.jp" />
                    <div className="space-y-1.5">
                        <label className={labelClass}>所属店舗 <span className="text-red-500">*</span></label>
                        <select
                            value={newStaffForm.storeId}
                            onChange={(e) => setNewStaffForm(f => ({ ...f, storeId: e.target.value }))}
                            className={selectClass + " font-bold shadow-sm"}
                        >
                            <option value="">店舗を選択してください</option>
                            {stores
                                .filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id))
                                .map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                            }
                        </select>
                    </div>
                    <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                        <button onClick={() => setIsAddModalOpen(false)} className="px-6 py-2.5 text-slate-400 font-bold hover:text-slate-600 transition-colors">キャンセル</button>
                        <button
                            onClick={handleAddStaff}
                            disabled={isSendingInvite || !newStaffForm.lastName.trim() || !newStaffForm.firstName.trim() || !newStaffForm.email.trim() || !newStaffForm.storeId}
                            className="px-8 py-2.5 font-bold rounded-[4px] shadow-lg transition-all flex items-center gap-2 active:scale-95 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:shadow-none disabled:cursor-not-allowed"
                        >
                            {isSendingInvite && <Loader2 size={16} className="animate-spin" />}
                            招待メールを送信
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="スタッフ詳細編集" maxWidth="max-w-2xl">
                {editingStaff && (
                    <div className="pt-2 space-y-6 max-h-[80vh] overflow-y-auto pr-2 custom-scrollbar no-scrollbar">
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="姓" requiredLabel value={editingStaff.lastName} onChange={(e) => setEditingStaff({...editingStaff, lastName: e.target.value})} />
                            <Input label="名" requiredLabel value={editingStaff.firstName} onChange={(e) => setEditingStaff({...editingStaff, firstName: e.target.value})} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input label="メールアドレス" requiredLabel type="email" value={editingStaff.email || ''} onChange={(e) => setEditingStaff({...editingStaff, email: e.target.value})} placeholder="example@shiftmate.jp" />
                            <Input label="電話番号" type="tel" value={editingStaff.phoneNumber || ''} onChange={(e) => setEditingStaff({...editingStaff, phoneNumber: e.target.value})} placeholder="09012345678" />
                            <Input label="従業員番号" value={editingStaff.employeeNumber || ''} onChange={(e) => setEditingStaff({...editingStaff, employeeNumber: e.target.value})} placeholder="例：E1001" />
                        </div>
                        
                        <StaffLoginStatus staff={editingStaff} stores={stores} onChanged={refreshStaff} variant="panel" />

                        <StaffDutyEditor staff={editingStaff} duties={duties} labelClass={labelClass} onChange={(dutyIds) => setEditingStaff({ ...editingStaff, dutyIds })} />

                        <div className="space-y-3">
                            <label className={labelClass}>シフト優先度</label>
                            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
                                {[
                                    { value: 'high', label: '高', color: 'bg-blue-600 text-white shadow-md' },
                                    { value: 'medium', label: '中', color: 'bg-amber-500 text-white shadow-md' },
                                    { value: 'low', label: '低', color: 'bg-red-500 text-white shadow-md' }
                                ].map(p => (
                                    <button
                                        key={p.value}
                                        onClick={() => setEditingStaff({ ...editingStaff, priority: p.value as Staff['priority'] })}
                                        className={`px-6 py-2 rounded-md text-xs font-black transition-all ${editingStaff.priority === p.value ? p.color : 'text-slate-400 hover:bg-white/50'}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className={labelClass}>所属拠点の選択（複数選択可）</label>
                            <div className="bg-slate-50 p-4 border border-slate-200 rounded-[4px] grid grid-cols-2 gap-2">
                                {stores.map(store => {
                                    const isSelected = editingStaff.stores?.includes(store.id);
                                    const isDisabled = !isSystemAdmin;
                                    return (
                                        <button
                                            key={store.id}
                                            disabled={isDisabled}
                                            onClick={() => {
                                                const current = editingStaff.stores || [];
                                                const next = isSelected ? current.filter(id => id !== store.id) : [...current, store.id];
                                                setEditingStaff({ ...editingStaff, stores: next });
                                            }}
                                            className={`px-4 py-3 rounded-[4px] text-xs font-bold border flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'} ${isDisabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        >
                                            <span className="truncate">{store.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className={labelClass}>権限ロール</label>
                                <select 
                                    disabled={!isSystemAdmin}
                                    value={editingStaff.role || 'staff'} 
                                    onChange={(e) => setEditingStaff({...editingStaff, role: e.target.value as Staff['role']})} 
                                    className={selectClass + " disabled:bg-slate-50 disabled:text-slate-400 font-bold shadow-sm"}
                                >
                                    <option value="system_admin">システム管理者</option>
                                    <option value="store_manager">店舗管理者</option>
                                    <option value="staff">スタッフ</option>
                                </select>
                            </div>
                            <div className="space-y-2"><label className={labelClass}>雇用ステータス</label><select value={editingStaff.status || 'active'} onChange={(e) => setEditingStaff({...editingStaff, status: e.target.value as Staff['status']})} className={selectClass + " font-bold shadow-sm"}><option value="active">在籍中</option><option value="on_leave">休職中</option><option value="resigned">退職済み</option></select></div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <Input label="時給（円）" type="number" min={0} value={editingStaff.hourlyWage} onChange={(e) => setEditingStaff({...editingStaff, hourlyWage: Number(e.target.value)})} className={selectClass + " font-bold shadow-sm"} />
                            <Input label="交通費（日額）" type="number" min={0} value={editingStaff.limit || 0} onChange={(e) => setEditingStaff({...editingStaff, limit: Number(e.target.value)})} className={selectClass + " font-bold shadow-sm"} />
                            <div className="space-y-2"><label className={labelClass}>有給残数（日）</label><input type="number" value={editingStaff.paidLeaveCount || 0} min={0} onChange={(e) => setEditingStaff({...editingStaff, paidLeaveCount: Number(e.target.value)})} className={selectClass + " font-bold shadow-sm"} /></div>
                        </div>

                        <div className="space-y-4">
                            <label className={labelClass}>ポジションとレベル設定</label>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {positions.map(pos => {
                                    const isSelected = (editingStaff.positionIds || []).includes(pos.id);
                                    const isStoreMatch = pos.storeIds.some(sid => editingStaff.stores?.includes(sid));
                                    if (!isStoreMatch) return null;
                                    return (
                                        <button 
                                            key={pos.id} 
                                            onClick={() => { 
                                                const currentIds = editingStaff.positionIds || []; 
                                                const nextIds = isSelected ? currentIds.filter(id => id !== pos.id) : [...currentIds, pos.id];
                                                
                                                let nextSkills = [...(editingStaff.skills || [])];
                                                if (!isSelected) {
                                                    nextSkills.push({ positionId: pos.id, level: 1 });
                                                } else {
                                                    nextSkills = nextSkills.filter(s => s.positionId !== pos.id);
                                                }
                                                
                                                setEditingStaff({ ...editingStaff, positionIds: nextIds, skills: nextSkills }); 
                                            }} 
                                            className={`px-4 py-2 rounded-[4px] text-xs font-bold border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                                        >
                                            {isSelected && <Check size={12} className="inline mr-1"/>}
                                            {pos.name}
                                        </button>
                                    );
                                })}
                            </div>

                            {editingStaff.positionIds?.length > 0 && (
                                <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">ポジション別レベル（0, 0.5, 1, 2）</h4>
                                    {editingStaff.positionIds.map(pid => {
                                        const pos = positions.find(p => p.id === pid);
                                        const skill = editingStaff.skills?.find(s => s.positionId === pid) || { level: 1 };
                                        if (!pos) return null;
                                        return (
                                            <div key={pid} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${pos.color.split(' ')[0]}`} />
                                                    <span className="text-xs font-bold text-slate-700">{pos.name}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 p-0.5 bg-slate-50 rounded-lg">
                                                    {[0, 0.5, 1, 2].map(lv => (
                                                        <button
                                                            key={lv}
                                                            onClick={() => handleLevelChange(pid, lv)}
                                                            className={`px-3 py-1.5 rounded-[4px] text-xs font-black transition-all ${skill.level === lv ? 'bg-blue-600 text-white shadow-sm scale-110' : 'text-slate-400 hover:bg-slate-200'}`}
                                                        >
                                                            {lv}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="pt-6 flex justify-end gap-3 border-t border-slate-100">
                            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-slate-400 font-bold hover:text-slate-600 transition-colors">キャンセル</button>
                            <button 
                                onClick={handleSaveRequest} 
                                disabled={isLoading || isSaveDisabled} 
                                className={`px-8 py-2.5 font-bold rounded-[4px] shadow-lg transition-all flex items-center gap-2 active:scale-95 ${isSaveDisabled ? 'bg-slate-300 text-white cursor-not-allowed shadow-none' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                            >
                                {isLoading && <Loader2 size={16} className="animate-spin" />}
                                保存する
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default StaffPage;
